import { useState, useCallback, ChangeEvent } from 'react';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { useStore } from '@src/stores-v2/StoreContext';
import { Artifact, FileData, PhotoTab } from '../types.ts/story';
import { formatUploadToArtifact } from '../utility/general';
import { updateStoryShowcase } from '../utility/story';

export type FileUploadType =
  | 'storyArtifacts'
  | 'storyArtifactsVideo'
  | 'organizationArtifacts'
  | 'organizationArtifactsVideo'
  | 'organizationLogos'
  | 'all';

export type FileUploadAcceptType = 'image' | 'video' | 'all';

interface UseFileUploadOptions {
  onUploadStart?: () => void;
  onUploadComplete?: (artifacts: Artifact[]) => void;
  onUploadError?: (error: any) => void;
  acceptType?: FileUploadAcceptType;
  uploadType?: FileUploadType;
  sizeLimitInMB?: number;
}

const PICTURE_FORMATS_TO_CONVERT = ['image/heic', 'image/heif', 'image/webp'];
const PICTURE_FORMATS = ['image/png', 'image/gif', 'image/jpeg', 'image/jpg'];
const ALL_PICTURE_FORMATS = PICTURE_FORMATS.concat(PICTURE_FORMATS_TO_CONVERT);
const VIDEO_FORMATS = ['video/mp4'];
const MAX_FILE_SIZE_MB = 400;

const getFileSizeInBytes = (mb: number) => {
  return mb * 1024 * 1024;
};

export const getUploadTypeFromPhotoTab = (tab: PhotoTab) => {
  switch (tab) {
    case PhotoTab.artifact:
      return 'storyArtifacts';
    case PhotoTab.orgArtifact:
      return 'organizationArtifacts';
    case PhotoTab.logo:
      return 'organizationLogos';
    default:
      return 'all';
  }
};

export const useFileUpload = (options: UseFileUploadOptions = {}) => {
  const {
    onUploadStart,
    onUploadComplete,
    onUploadError,
    acceptType = 'all',
    uploadType = 'all',
    sizeLimitInMB = MAX_FILE_SIZE_MB,
  } = options;

  const videoCreator = useVideoCreatorStore();
  const { datoClientStore } = useStore();
  const [isUploading, setIsUploading] = useState(false);

  const getAcceptFormats = useCallback(() => {
    switch (uploadType) {
      case 'storyArtifacts':
      case 'organizationArtifacts':
      case 'organizationLogos':
        return ALL_PICTURE_FORMATS;
      case 'storyArtifactsVideo':
      case 'organizationArtifactsVideo':
        return VIDEO_FORMATS;
      default:
        return acceptType === 'image'
          ? ALL_PICTURE_FORMATS
          : acceptType === 'video'
            ? VIDEO_FORMATS
            : ALL_PICTURE_FORMATS.concat(VIDEO_FORMATS);
    }
  }, [acceptType, uploadType]);

  const uploadFile = useCallback(
    async (file: File, skipSizeCheck?: boolean): Promise<Artifact> => {
      if (!skipSizeCheck && file.size > getFileSizeInBytes(sizeLimitInMB)) {
        videoCreator.toastState = {
          state: 'error',
          message: `${sizeLimitInMB}MB file size limit. Please try a smaller file size.`,
        };
        throw new Error('File size limit exceeded');
      }
      const fileName = file.name.split('.')[0];
      const newFileData: FileData & { fileName: string } = {
        type: 'artifact',
        file,
        fileName,
        alt: '',
        title: '',
      };
      const newUpload =
        await datoClientStore.assetRepository?.uploadFile(newFileData);
      return formatUploadToArtifact(newUpload!, {});
    },
    [datoClientStore.assetRepository, sizeLimitInMB],
  );

  const handleUploadFiles = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const files = [...(e.target.files || [])];
      if (!files.length) return;

      try {
        setIsUploading(true);
        onUploadStart?.();

        if (
          files.some((file) => file.size > getFileSizeInBytes(sizeLimitInMB))
        ) {
          videoCreator.toastState = {
            state: 'error',
            message: `${sizeLimitInMB}MB file size limit. Please try a smaller file size.`,
          };
          throw new Error('File size limit exceeded');
        }
        const uploadResults = (
          await Promise.all(files.map((file) => uploadFile(file, true)))
        ).filter(Boolean) as Artifact[];

        const photoUploads =
          uploadResults.filter((u) => u?.responsiveImage).filter(Boolean) || [];
        const videoUploads =
          uploadResults.filter((u) => !u.responsiveImage).filter(Boolean) || [];

        const storyData = videoCreator.story;

        // Handle story artifacts
        if (
          (photoUploads?.length || videoUploads?.length) &&
          (uploadType === 'all' ||
            uploadType === 'storyArtifactsVideo' ||
            uploadType === 'storyArtifacts')
        ) {
          const existingPhotos = storyData?.storyArtifacts || [];
          const existingVids = storyData?.storyArtifactsVideo || [];

          storyData!.storyArtifactsVideo! = [
            ...existingVids,
            ...videoUploads,
          ] as Artifact[];
          storyData!.storyArtifacts! = [
            ...existingPhotos,
            ...photoUploads,
          ] as Artifact[];

          await videoCreator.updateStory(storyData!);

          if (photoUploads?.length) {
            videoCreator.statusUpdateManager.addStoryArtifactsToWatch(
              storyData!.id,
              photoUploads,
            );
          }
        }

        // Handle organization artifacts
        if (
          (photoUploads?.length || videoUploads?.length) &&
          (uploadType === 'organizationArtifactsVideo' ||
            uploadType === 'organizationArtifacts' ||
            uploadType === 'organizationLogos') &&
          storyData?.primaryShowcase
        ) {
          const albumId = storyData.primaryShowcase.id;
          const albumIdx = storyData._allReferencingShowcases.findIndex(
            (s) => s.id === albumId,
          );
          const album =
            albumIdx > -1
              ? storyData._allReferencingShowcases[albumIdx]
              : storyData.primaryShowcase;

          const existingPhotos = album?.organizationArtifacts || [];
          const existingVids = album?.organizationArtifactsVideo || [];
          const existingLogos = album?.organizationLogos || [];

          album!.organizationArtifactsVideo = [
            ...existingVids,
            ...videoUploads,
          ] as Artifact[];

          if (uploadType === 'organizationLogos') {
            album!.organizationLogos = [
              ...existingLogos,
              ...photoUploads,
            ] as Artifact[];
          } else {
            album!.organizationArtifacts = [
              ...existingPhotos,
              ...photoUploads,
            ] as Artifact[];
          }

          await datoClientStore.albumRepository?.update(album);
          if (videoCreator.organization?.id === album.id)
            videoCreator.organization = album;
          updateStoryShowcase(storyData, album);

          if (photoUploads?.length) {
            videoCreator.statusUpdateManager.addOrganizationArtifactsToWatch(
              album.id,
              photoUploads,
            );
          }
        }

        videoCreator.story = storyData;
        onUploadComplete?.([...photoUploads, ...videoUploads]);

        // Return the first uploaded file for single file uploads
        return uploadResults[0];
      } catch (error) {
        console.log('Error: ', error);
        onUploadError?.(error);
      } finally {
        setIsUploading(false);
      }
    },
    [
      uploadFile,
      videoCreator,
      datoClientStore,
      uploadType,
      onUploadStart,
      onUploadComplete,
      onUploadError,
    ],
  );

  const uploadSingleFile = useCallback(
    async (file: File): Promise<Artifact | undefined> => {
      try {
        setIsUploading(true);
        onUploadStart?.();
        const artifact = await uploadFile(file);
        return artifact;
      } catch (error) {
        console.error('Error uploading file:', error);
        onUploadError?.(error);
      } finally {
        setIsUploading(false);
      }
    },
    [uploadFile, onUploadStart, onUploadError],
  );

  const handleUploadFile = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const files = [...(e.target.files || [])];
      if (!files.length) return;
      try {
        setIsUploading(true);
        onUploadStart?.();
        const storyData = videoCreator.story;
        const artifact = await uploadSingleFile(files[0]);
        if (storyData && artifact) {
          if (uploadType === 'storyArtifacts') {
            const storyArtifacts = storyData.storyArtifacts || [];
            storyData.storyArtifacts = [...storyArtifacts, artifact];
            await videoCreator.updateStory(storyData);
            if (artifact.responsiveImage) {
              videoCreator.statusUpdateManager.addStoryArtifactsToWatch(
                storyData.id,
                [artifact],
              );
            }
          } else if (
            uploadType === 'organizationArtifacts' &&
            videoCreator.organization
          ) {
            const organization = videoCreator.organization;
            const orgArtifacts = organization.organizationArtifacts || [];
            organization.organizationArtifacts = [...orgArtifacts, artifact];
            await datoClientStore.albumRepository?.update(organization);
            updateStoryShowcase(storyData, organization);
            if (artifact.responsiveImage) {
              videoCreator.statusUpdateManager.addOrganizationArtifactsToWatch(
                organization.id,
                [artifact],
              );
            }
          } else if (
            uploadType === 'organizationLogos' &&
            videoCreator.organization
          ) {
            const organization = videoCreator.organization;
            const orgLogos = organization.organizationLogos || [];
            organization.organizationLogos = [...orgLogos, artifact];
            await datoClientStore.albumRepository?.update(organization);
            updateStoryShowcase(storyData, organization);
          }
        }
        onUploadComplete?.([artifact!]);
      } catch (error) {
        console.log('Error: ', error);
        onUploadError?.(error);
      } finally {
        setIsUploading(false);
      }
    },
    [
      uploadSingleFile,
      videoCreator,
      datoClientStore,
      uploadType,
      onUploadStart,
      onUploadComplete,
      onUploadError,
    ],
  );

  return {
    isUploading,
    handleUploadFiles,
    handleUploadFile,
    acceptFormats: getAcceptFormats(),
  };
};
